import {
  ERRORS,
  NETWORK_BITRATE_TEST_TIMEOUT,
  NETWORK_PREFLIGHT_API_EVENTS,
} from 'frontend/constants/twilio';
import { MediaConnectionBitrateTest, testMediaConnectionBitrate } from '@twilio/rtc-diagnostics';
import {
  NETWORK_BITRATE_TEST_VALUES_LIMIT,
  NETWORK_QUALITY_SCORE,
  getQualityScore,
} from 'frontend/utils/network-connection';
import { all, task, waitForProperty } from 'ember-concurrency';
import { fetchIceServers } from 'frontend/utils/fetch-data';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { later } from '@ember/runloop';
import { reads } from '@ember/object/computed';
import { runPreflight } from 'twilio-video';
import { set, setProperties } from '@ember/object';
import Service, { service } from '@ember/service';
import classic from 'ember-classic-decorator';

@classic
export default class TwilioPreflightService extends Service {
  @service('twilio/error-handling') errorHandling;
  @service session;
  @service dataFetcher;

  @reads('session.roomModel') roomModel;

  networkPreflightResult;
  networkBitrateResult;
  bitrateArray = [];
  networkQualityScore;
  iceServers;
  networkBitrateTestTimeout = NETWORK_BITRATE_TEST_TIMEOUT;

  init() {
    super.init(...arguments);
    this.#fetchIceServersData();
  }

  @(task(function* () {
    let { testToken } = this.roomModel;
    let iceServers = yield waitForProperty(this, 'iceServers');

    this.runPreflightNetworkTest(testToken);
    this.runBitrateNetworkTest(iceServers);

    let [bitrateResult, preflightResult] = yield all([
      waitForProperty(this, 'networkBitrateResult'),
      waitForProperty(this, 'networkPreflightResult'),
    ]);
    let totalQualityScore = getQualityScore(preflightResult, bitrateResult);
    setProperties(this, {
      networkPreflightResult: null,
      networkBitrateResult: null,
      bitrateArray: [],
      networkQualityScore: totalQualityScore,
    });

    if (isEmberTesting()) return;
    this.testNetworkConnection.perform();
  }).restartable())
  testNetworkConnection;

  runPreflightNetworkTest(token) {
    let preflightTest = this.runPreflightTest(token);

    preflightTest.on(NETWORK_PREFLIGHT_API_EVENTS.failed, err => {
      set(
        this,
        'networkQualityScore',
        err.code === ERRORS.signalingConnectionError
          ? NETWORK_QUALITY_SCORE.poor
          : NETWORK_QUALITY_SCORE.suboptimal
      );
      later(() => {
        this.testNetworkConnection.perform();
      }, 1000);
    });
    preflightTest.on(NETWORK_PREFLIGHT_API_EVENTS.completed, report => {
      set(this, 'networkPreflightResult', report);
    });
  }

  runBitrateNetworkTest(iceServers) {
    let bitrateTest = this.runBitrateTest(iceServers);

    bitrateTest.on(MediaConnectionBitrateTest.Events.Error, () => {
      set(this, 'networkQualityScore', NETWORK_QUALITY_SCORE.suboptimal);
      later(() => {
        this.testNetworkConnection.perform();
      }, 1000);
    });
    bitrateTest.on(MediaConnectionBitrateTest.Events.Bitrate, bitrate => {
      this.bitrateArray.push(bitrate);
      if (this.bitrateArray.length >= NETWORK_BITRATE_TEST_VALUES_LIMIT) bitrateTest.stop();
    });
    bitrateTest.on(MediaConnectionBitrateTest.Events.End, report => {
      set(this, 'networkBitrateResult', report);
    });

    later(() => bitrateTest.stop(), this.networkBitrateTestTimeout);
  }

  runPreflightTest(token) {
    return runPreflight(token);
  }

  runBitrateTest(iceServers) {
    return testMediaConnectionBitrate({ iceServers });
  }

  async #fetchIceServersData() {
    try {
      let iceServers = await this.dataFetcher.fetchWithRetry.perform(fetchIceServers);

      set(this, 'iceServers', iceServers);
    } catch (e) {
      this.errorHandling.notifyError(e);
    }
  }
}
